import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, errMsg, currencyInfo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CountrySelect } from "@/components/CountrySelect";
import { LogoField, ShareShop } from "@/pages/seller/ShopBranding";

const Zones = ({ zones, setZones, shop }) => {
  const { label, decimals } = currencyInfo(shop.currency);
  return (
  <div className="space-y-2" data-testid="delivery-zones">
    {zones.map((z, i) => {
      const upd = (k, v) => setZones(zones.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
      return (
        <div key={i} className="grid gap-2 rounded-lg border p-2 sm:grid-cols-[1fr_1fr_110px_120px_auto] sm:border-0 sm:p-0">
          <CountrySelect testid={`zone-country-${i}`} need="delivery" value={z.country_code || shop.country_code} onChange={(v) => upd("country_code", v)} />
          <Input data-testid={`zone-name-${i}`} placeholder="Zone (ex : Dakar Plateau)" value={z.name} onChange={(e) => upd("name", e.target.value)} />
          <Input data-testid={`zone-fee-${i}`} type="number" min="0" step={decimals ? 1 / 10 ** decimals : 1} placeholder={`Frais (${label})`} value={z.fee} onChange={(e) => upd("fee", e.target.value)} />
          <Input data-testid={`zone-delay-${i}`} placeholder="Délai (24 h)" value={z.delay} onChange={(e) => upd("delay", e.target.value)} />
          <Button variant="ghost" size="icon" onClick={() => setZones(zones.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
        </div>
      );
    })}
    <Button variant="outline" size="sm" onClick={() => setZones([...zones, { name: "", fee: 0, delay: "", country_code: shop.country_code }])} data-testid="zone-add"><Plus className="mr-1 h-3 w-3" />Ajouter une zone</Button>
    <p className="text-xs text-gray-500">Les commandes ne sont acceptées que dans les pays où la livraison est ouverte (actuellement : Sénégal) et dans le pays de votre boutique.</p>
  </div>
  );
};

export default function SellerShop() {
  const { shop, reloadShop } = useOutletContext();
  const [f, setF] = useState({ name: shop.name, description: shop.description, phone: shop.phone, city: shop.city, address: shop.address || "", whatsapp: shop.whatsapp || "", logo: shop.logo, banner: shop.banner });
  const [conditions, setConditions] = useState(shop.delivery?.conditions || "");
  const [zones, setZones] = useState(shop.delivery?.zones || []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (k, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", k === "logo" ? "logo" : "photo");
    try { const { data } = await api.post("/seller/uploads", fd); setF((x) => ({ ...x, [k]: data.url })); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    try {
      await api.put("/seller/shop", { ...f, delivery: { conditions, zones: zones.filter((z) => z.name).map((z) => ({ ...z, fee: Number(z.fee) || 0 })) } });
      toast.success("Boutique enregistrée");
      reloadShop();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="seller-shop" className="space-y-6">
      <h1 className="font-display text-3xl font-black tracking-tight">Boutique & livraison</h1>
      <ShareShop shop={shop} />
      <div className="grid gap-4 rounded-2xl border bg-white p-5 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="shop-name-input" value={f.name} onChange={set("name")} /></div>
        <div className="space-y-1.5"><Label>Téléphone (privé, visible par l'admin)</Label><Input data-testid="shop-phone-input" value={f.phone} onChange={set("phone")} /></div>
        <div className="space-y-1.5"><Label htmlFor="shop-whatsapp">Numéro WhatsApp pour les commandes</Label><Input id="shop-whatsapp" data-testid="shop-whatsapp-input" inputMode="tel" placeholder="+221 77 123 45 67" value={f.whatsapp} onChange={set("whatsapp")} />
          <p className="text-xs text-gray-500">Format international. Après une commande, le client pourra vous prévenir sur ce numéro via un message WhatsApp pré-rempli (il l'envoie lui-même).</p></div>
        <div className="space-y-1.5"><Label>Ville</Label><Input data-testid="shop-city-input" value={f.city} onChange={set("city")} /></div>
        <div className="space-y-1.5"><Label>Adresse</Label><Input value={f.address} onChange={set("address")} /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea data-testid="shop-description-input" rows={3} value={f.description} onChange={set("description")} /></div>
        <LogoField name={f.name} logo={f.logo} onUpload={(file) => upload("logo", file)} onRemove={() => setF((x) => ({ ...x, logo: null }))} />
        <div className="space-y-1.5"><Label>Bannière</Label><Input data-testid="shop-banner-input" type="file" accept="image/*" onChange={(e) => e.target.files[0] && upload("banner", e.target.files[0])} /></div>
      </div>
      <div className="space-y-4 rounded-2xl border bg-white p-5">
        <h2 className="font-display text-lg font-bold">Zones, frais et délais de livraison</h2>
        <Zones zones={zones} setZones={setZones} shop={shop} />
        <div className="space-y-1.5"><Label>Conditions de livraison</Label><Textarea data-testid="delivery-conditions" rows={3} value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="Ex : livraison après confirmation téléphonique, retours sous 48 h…" /></div>
      </div>
      <Button onClick={save} className="rounded-full" size="lg" data-testid="shop-save">Enregistrer</Button>
    </div>
  );
}
